import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';

const fmtDate = iso => {
  if (!iso) return '—';
  try {
    return dayjs(iso).format('DD.MM.YYYY HH:mm');
  } catch (_) {
    return String(iso);
  }
};

const fmtDay = value => {
  if (!value) return '—';
  try {
    const d = dayjs(value);
    if (!d.isValid()) return String(value);
    return d.format('DD.MM.YYYY');
  } catch (_) {
    return String(value);
  }
};

const FILTER_PARAM_LABELS = {
  branchScope: 'Standort',
  voucherProductId: 'Gutschein',
  resourceVoucherProductId: 'Ressourcengutschein',
  discountCodeId: 'Rabattcode',
  entertainmentId: 'Entertainment',
  entertainmentTypeId: 'Entertainment-Typ',
  entertainmentType: 'Entertainment-Typ',
  packageId: 'Paket',
  occasion: 'Anlass',
  excludeRegiondo: 'Regiondo',
};

// German labels of the occasion filter (contract: statistic occasion dimension).
const FILTER_OCCASION_LABELS = {
  childBirthday: 'Kindergeburtstag',
  adultBirthday: 'Geburtstag (Erwachsene)',
  bachelorParty: 'JGA',
  teamEvent: 'Teamevent',
  schoolTrip: 'Schulausflug',
  none: 'Ohne Anlass',
};

const resolveFilterValue = (key, value, lookups) => {
  if (value == null || value === '') return null;
  // Internal cache discriminators are not filters the user chose.
  if (key === 'computeVersion') return null;
  if (key === 'branchScope' && value === 'all') return 'Alle Standorte';
  // Filters left at "no restriction" only add noise to the entry label.
  if ((key === 'entertainmentType' || key === 'occasion') && value === 'all') return null;
  if (key === 'occasion') return FILTER_OCCASION_LABELS[String(value)] || String(value);
  if (key === 'excludeRegiondo') {
    return value === true || value === 'true' ? 'ausgeschlossen' : null;
  }
  const lookup = lookups && lookups[key];
  if (lookup && typeof lookup === 'object') {
    const hit = lookup[String(value)] || lookup[value];
    if (hit) return String(hit);
  }
  return String(value);
};

const isHidden = (hiddenOpt, value) => {
  if (typeof hiddenOpt === 'function') return !!hiddenOpt(value);
  return !!hiddenOpt;
};

const formatEntryLabel = (entry, lookups = {}, paramConfig = {}) => {
  if (!entry || !entry.filterParams) return entry?._id || '?';
  const p = entry.filterParams;
  const parts = [];
  if (p.startDate && p.endDate) {
    parts.push(`${fmtDay(p.startDate)} – ${fmtDay(p.endDate)}`);
  } else if (p.startDate) {
    parts.push(`ab ${fmtDay(p.startDate)}`);
  } else if (p.endDate) {
    parts.push(`bis ${fmtDay(p.endDate)}`);
  }

  const dateKeys = new Set(['startDate', 'endDate']);
  const orderedKeys = [
    'branchScope',
    ...Object.keys(p).filter(k => !dateKeys.has(k) && k !== 'branchScope'),
  ];
  const seen = new Set();
  for (const key of orderedKeys) {
    if (seen.has(key) || !(key in p)) continue;
    seen.add(key);
    const cfg = paramConfig && paramConfig[key];
    if (cfg && isHidden(cfg.hidden, p[key])) continue;
    let resolved;
    if (cfg && typeof cfg.format === 'function') {
      resolved = cfg.format(p[key], (lookups && lookups[key]) || null);
    } else {
      resolved = resolveFilterValue(key, p[key], lookups);
    }
    if (resolved == null || resolved === '') continue;
    const label = (cfg && cfg.label) || FILTER_PARAM_LABELS[key] || key;
    parts.push(`${label}: ${resolved}`);
  }

  parts.push(fmtDate(entry.computedAt));
  return parts.join(' · ');
};

const REFRESH_TOOLTIP =
  'Cache umgehen und Statistik neu berechnen. Die vorhandenen Daten bleiben sichtbar, bis die Neuberechnung fertig ist.';

const RefreshButton = React.memo(function RefreshButton({ busy, disabled, onClick }) {
  return (
    <Tooltip title={REFRESH_TOOLTIP} disableInteractive>
      <span>
        <Button
          variant='outlined'
          size='small'
          color='success'
          startIcon={
            busy ? (
              <CircularProgress size={16} color='success' />
            ) : (
              <RefreshIcon fontSize='small' />
            )
          }
          onClick={onClick}
          disabled={disabled}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Neu berechnen
        </Button>
      </span>
    </Tooltip>
  );
});

export const StatisticStatusBar = ({
  status,
  isStale,
  cacheComputedAt,
  cacheExpiresAt,
  job,
  error,
  entries,
  selectedEntryId,
  onSelectEntry,
  entriesLoading,
  onRefresh,
  refreshDisabled = false,
  filterParamLookups,
  filterParamConfig,
  currentUserId,
}) => {
  const canRefresh = typeof onRefresh === 'function';
  const refreshBusy =
    status === 'fetching' || status === 'pending' || status === 'running';
  const refreshButtonDisabled = refreshDisabled || refreshBusy || !canRefresh;
  const handleRefreshClick = React.useCallback(() => {
    if (canRefresh) onRefresh();
  }, [canRefresh, onRefresh]);

  const sortedEntries = useMemo(() => {
    if (!Array.isArray(entries)) return [];
    const uid = currentUserId ? String(currentUserId) : null;
    const byRelevance = [...entries].sort((a, b) => {
      const aOwn = uid && a.requestedByUserId && String(a.requestedByUserId) === uid ? 1 : 0;
      const bOwn = uid && b.requestedByUserId && String(b.requestedByUserId) === uid ? 1 : 0;
      if (aOwn !== bOwn) return bOwn - aOwn;
      const ta = (a.requestedAt ? new Date(a.requestedAt) : a.computedAt ? new Date(a.computedAt) : new Date(0)).getTime();
      const tb = (b.requestedAt ? new Date(b.requestedAt) : b.computedAt ? new Date(b.computedAt) : new Date(0)).getTime();
      return tb - ta;
    });
    const seen = new Set();
    const deduped = [];
    for (const entry of byRelevance) {
      const params = entry.filterParams || {};
      const signature = Object.keys(params)
        .sort()
        .map(k => `${k}=${String(params[k])}`)
        .join('|');
      if (seen.has(signature)) continue;
      seen.add(signature);
      deduped.push(entry);
    }
    return deduped;
  }, [entries, currentUserId]);

  const action = (
    <RefreshButton
      busy={refreshBusy}
      disabled={refreshButtonDisabled}
      onClick={handleRefreshClick}
    />
  );

  let alertNode = null;
  if (status === 'fetching') {
    alertNode = (
      <Alert severity='info' icon={<CircularProgress size={16} />} action={action}>
        Versuche Statistik aus Cache zu laden
      </Alert>
    );
  } else if (status === 'pending' || status === 'running') {
    const progress = job && Number.isFinite(job.progress) ? job.progress : null;
    const progressLabel = job && typeof job.progressLabel === 'string' ? job.progressLabel.trim() : '';
    const msg = 'Statistik wird auf dem Server berechnet. Du kannst dieses Fenster in der Zwischenzeit schließen.';
    alertNode = (
      <Alert severity='info' icon={<CircularProgress size={16} />} action={action}>
        <Stack spacing={1}>
          <Typography variant='body2'>
            {msg}
          </Typography>
          {progressLabel && (
            <Typography variant='caption' color='text.secondary'>
              {progressLabel}
              {progress != null ? ` (${Math.min(100, Math.max(0, progress))} %)` : ''}
            </Typography>
          )}
          {progress != null && progress > 0 && (
            <LinearProgress variant='determinate' value={Math.min(100, Math.max(0, progress))} />
          )}
        </Stack>
      </Alert>
    );
  } else if (status === 'stale') {
    alertNode = (
      <Alert severity='warning' action={action}>
        Veralteter Cache-Eintrag wird angezeigt (berechnet {fmtDate(cacheComputedAt)}). Aktualisierung läuft
        im Hintergrund – du wirst benachrichtigt, sobald die Statistik bereit ist.
      </Alert>
    );
  } else if (status === 'failed') {
    alertNode = (
      <Alert severity='error' action={action}>
        Berechnung fehlgeschlagen{error ? `: ${error}` : '.'}
      </Alert>
    );
  } else if (status === 'fresh') {
    alertNode = (
      <Alert severity='success' variant='outlined' action={action}>
        Aktuelle Daten (berechnet {fmtDate(cacheComputedAt)}).
      </Alert>
    );
  } else {
    // Idle — no fetch has been issued yet (or the view hasn't applied
    // any filter yet). There's nothing meaningful to refresh, so we
    // don't render a lone "Neu berechnen" button — the view's own
    // Apply button is the primary action in that state.
    alertNode = null;
  }

  const showEntriesDropdown = sortedEntries.length >= 2;
  const showStaleNote = isStale && status !== 'stale';

  if (!alertNode && !showEntriesDropdown && !showStaleNote) {
    return null;
  }

  return (
    <Stack spacing={1.5} sx={{ mb: 2 }}>
      {alertNode}
      {showEntriesDropdown && (
        <FormControl size='small' sx={{ minWidth: 320, maxWidth: '100%' }}>
          <InputLabel>Verfügbare Cache-Einträge</InputLabel>
          <Select
            value={selectedEntryId || ''}
            label='Verfügbare Cache-Einträge'
            onChange={evt => onSelectEntry && onSelectEntry(evt.target.value || null)}
            disabled={entriesLoading}
          >
            <MenuItem value=''>
              <em>Nach aktuellen Filtern (Auto)</em>
            </MenuItem>
            {sortedEntries.map(entry => (
              <MenuItem key={entry._id} value={entry._id}>
                {formatEntryLabel(entry, filterParamLookups, filterParamConfig)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {showStaleNote && (
        <Typography variant='caption' color='text.secondary'>
          Hinweis: Angezeigte Daten stammen aus einem veralteten Cache-Eintrag.
        </Typography>
      )}
    </Stack>
  );
};

export default StatisticStatusBar;
